import React, { useState } from 'react';
import './DataBackup.css';
import apiService from '../services/apiService';

function DataBackup() {
  const [exportStatus, setExportStatus] = useState('');
  const [importStatus, setImportStatus] = useState('');

  // Fonction pour exporter toutes les données
  const exportAllData = async () => {
    try {
      setExportStatus('Export en cours...');
      
      // Récupérer toutes les données via API service
      const dataToExport = await apiService.exportData();
      
      // Convertir en chaîne JSON et créer un blob
      const jsonString = JSON.stringify(dataToExport, null, 2);
      const blob = new Blob([jsonString], { type: 'application/json' });
      
      // Créer un lien de téléchargement
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      
      // Configurer le lien de téléchargement
      const date = new Date().toISOString().slice(0, 10);
      const time = new Date().toISOString().slice(11, 19).replace(/:/g, '-');
      link.href = url;
      link.setAttribute('download', `amazon-stock-backup-${date}_${time}.json`);
      
      // Déclencher le téléchargement
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      
      setExportStatus('Sauvegarde réussie !');
      setTimeout(() => setExportStatus(''), 3000);
    } catch (error) {
      console.error('Erreur lors de l\'export des données:', error);
      setExportStatus('Erreur lors de la sauvegarde.');
      setTimeout(() => setExportStatus(''), 3000);
    }
  };

  // Fonction pour importer toutes les données
  const importAllData = (event) => {
    const file = event.target.files[0];
    if (!file) return;
    
    const reader = new FileReader();
    
    reader.onload = async (e) => {
      try {
        setImportStatus('Import en cours...');
        const importedData = JSON.parse(e.target.result);
        
        // Vérifier que les données sont valides
        if (!importedData.exportDate) {
          throw new Error('Le fichier ne semble pas être un fichier de sauvegarde valide.');
        }
        
        // Si les données proviennent de l'ancienne version (localStorage format)
        if (importedData.appVersion === '1.0' || importedData.appVersion === '1.1') {
          // Import via migration endpoint
          try {
            const response = await fetch('/api/migrate/localStorage', {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
              },
              body: JSON.stringify(importedData)
            });
            
            if (!response.ok) {
              throw new Error('Migration failed');
            }
            
            const result = await response.json();
            console.log('Import successful:', result);
            
            setImportStatus('Données importées avec succès !');
            setTimeout(() => {
              setImportStatus('');
              window.location.reload();
            }, 2000);
          } catch (apiError) {
            console.log('API import failed, falling back to localStorage:', apiError);
            // Fallback to localStorage import
            importToLocalStorage(importedData);
          }
        } else {
          // New format - direct API import would go here
          // For now, fallback to localStorage
          importToLocalStorage(importedData);
        }
      } catch (error) {
        console.error('Erreur lors de l\'import des données:', error);
        setImportStatus('Erreur lors de la restauration: ' + error.message);
        setTimeout(() => setImportStatus(''), 5000);
      }
    };
    
    reader.onerror = () => {
      setImportStatus('Erreur lors de la lecture du fichier.');
      setTimeout(() => setImportStatus(''), 3000);
    };
    
    reader.readAsText(file);
  };
  
  const importToLocalStorage = (importedData) => {
    try {
      // Restaurer les données d'inventaire comptable
      if (importedData.inventoryPrices) {
        localStorage.setItem('inventoryPrices', JSON.stringify(importedData.inventoryPrices));
      }
      
      // Restaurer les données de stock manuel
      if (importedData.stockAtHome) {
        localStorage.setItem('stockAtHome', JSON.stringify(importedData.stockAtHome));
      }
      
      if (importedData.stockInTransit) {
        localStorage.setItem('stockInTransit', JSON.stringify(importedData.stockInTransit));
      }
      
      // Restaurer la configuration des fournisseurs
      if (importedData.supplierConfig) {
        localStorage.setItem('supplierConfig', JSON.stringify(importedData.supplierConfig));
      }
      
      // Restaurer les paramètres de délais
      if (importedData.fbaTransferDelay) {
        localStorage.setItem('fbaTransferDelay', importedData.fbaTransferDelay.toString());
      }
      
      if (importedData.supplierTransitDelay) {
        localStorage.setItem('supplierTransitDelay', importedData.supplierTransitDelay.toString());
      }
      
      // Restaurer les données ROI
      if (importedData.roiPurchasePrices) {
        localStorage.setItem('roiPurchasePrices', JSON.stringify(importedData.roiPurchasePrices));
      }
      
      if (importedData.roiTimeSettings) {
        localStorage.setItem('roiTimeSettings', JSON.stringify(importedData.roiTimeSettings));
      }
      
      if (importedData.roiFeeSettings) {
        localStorage.setItem('roiFeeSettings', JSON.stringify(importedData.roiFeeSettings));
      }
      
      if (importedData.roiHourlyRate) {
        localStorage.setItem('roiHourlyRate', importedData.roiHourlyRate);
      }
      
      if (importedData.suppliesStock) {
        localStorage.setItem('suppliesStock', JSON.stringify(importedData.suppliesStock));
      }
      
      // Clear migration flag to trigger re-migration
      localStorage.removeItem('migrated_to_database');
      
      setImportStatus('Données restaurées avec succès !');
      setTimeout(() => {
        setImportStatus('');
        window.location.reload();
      }, 2000);
    } catch (error) {
      console.error('Erreur lors de la restauration localStorage:', error);
      setImportStatus('Erreur lors de la restauration.');
      setTimeout(() => setImportStatus(''), 3000);
    }
  };

  return (
    <div className="data-backup">
      <div className="backup-buttons">
        <button 
          onClick={exportAllData} 
          className="backup-button export-button"
          title="Sauvegarder toutes vos données (prix, stock, fournisseurs...)"
        >
          Sauvegarder Données
        </button>
        
        <div className="import-container">
          <input
            type="file"
            id="import-file"
            accept=".json"
            onChange={importAllData}
            style={{ display: 'none' }}
          />
          <label 
            htmlFor="import-file" 
            className="backup-button import-button"
            title="Restaurer vos données depuis une sauvegarde"
          >
            Restaurer Données
          </label>
        </div>
      </div>
      
      {exportStatus && <div className="backup-status success">{exportStatus}</div>}
      {importStatus && <div className="backup-status success">{importStatus}</div>}
    </div>
  );
}

export default DataBackup;